import React from 'react';
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Github,
  Youtube,
  ExternalLink,
} from 'lucide-react';
import { ButtonLink } from './shared/ButtonLink';
import type { FooterComponent as FooterComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';

interface FooterComponentProps {
  component: FooterComponentType;
}

// Social platform icon mapping
const socialIconMap = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  github: Github,
  youtube: Youtube,
} as const;

export const FooterComponent: React.FC<FooterComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Color priority: customColors (user explicit) > theme/brand package > stored data defaults
  const footerBgColor =
    data.customColors?.background ||
    themeStyles.footer?.background ||
    data.backgroundColor ||
    '#111827';
  const footerTextColor =
    data.customColors?.text || themeStyles.footer?.text || data.textColor || '#ffffff';
  const footerMutedText =
    data.customColors?.mutedText || themeStyles.footer?.mutedText || '#9CA3AF';
  const footerLinkHover =
    data.customColors?.linkHover || themeStyles.footer?.linkHover || '#DC2626';

  // Group links by category with null check
  const groupedLinks = (data.links || []).reduce(
    (acc, link) => {
      const category = link.category || 'General';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(link);
      return acc;
    },
    {} as Record<string, typeof data.links>
  );

  const getSocialIcon = (platform: string) => {
    const normalizedPlatform = platform.toLowerCase();
    return socialIconMap[normalizedPlatform as keyof typeof socialIconMap] || ExternalLink;
  };

  // Check if background is a gradient
  const isGradient =
    footerBgColor?.startsWith('linear-gradient') || footerBgColor?.startsWith('radial-gradient');

  const footerStyles: React.CSSProperties = {
    ...(isGradient ? { backgroundImage: footerBgColor } : { backgroundColor: footerBgColor }),
    color: footerTextColor,
    // Force the background to override any inherited styles
    position: 'relative',
    isolation: 'isolate',
  };

  return (
    <>
      <style>
        {`
          footer[data-theme-footer="true"] {
            ${
              isGradient
                ? `background-image: ${footerBgColor} !important;`
                : `background-color: ${footerBgColor} !important;`
            }
            color: ${footerTextColor} !important;
          }
          footer[data-theme-footer="true"] .footer-hover-link:hover { color: ${footerLinkHover} !important; text-decoration: none !important; }
          footer[data-theme-footer="true"] .footer-hover-social:hover { background-color: ${footerLinkHover} !important; }
        `}
      </style>
      <footer data-theme-footer="true" style={footerStyles}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="py-12 md:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Brand Section */}
              <div className="lg:col-span-2">
                <h3
                  className="text-2xl font-bold mb-4"
                  style={{ color: footerTextColor, ...headingFontStyle }}
                >
                  {data.siteName || 'Website'}
                </h3>
                {data.description && (
                  <p
                    className="mb-6 max-w-md leading-relaxed"
                    style={{ color: footerMutedText, ...bodyFontStyle }}
                    dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.description) }}
                  />
                )}

                {/* Social Links */}
                {(data.socialLinks || []).length > 0 && (
                  <div className="flex space-x-4">
                    {(data.socialLinks || []).map((social, index) => {
                      const IconComponent = getSocialIcon(social.platform);
                      return (
                        <a
                          key={index}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="footer-hover-social w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200"
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: footerTextColor,
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = footerLinkHover;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                          }}
                          aria-label={`Visit our ${social.platform} page`}
                        >
                          <IconComponent className="w-5 h-5" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Link Categories */}
              {Object.entries(groupedLinks).map(([category, links]) => (
                <div key={category}>
                  <h4
                    className="text-lg font-semibold mb-4"
                    style={{ color: footerTextColor, ...headingFontStyle }}
                  >
                    {category}
                  </h4>
                  <ul className="space-y-3">
                    {links.map((link, index) => (
                      <li key={index}>
                        <ButtonLink
                          url={link.url}
                          className="footer-hover-link transition-colors duration-200 inline-block"
                          style={{ color: footerMutedText }}
                          onMouseEnter={e => {
                            e.currentTarget.style.color = footerLinkHover;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.color = footerMutedText;
                          }}
                        >
                          {link.text}
                        </ButtonLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="py-6" style={{ borderTop: `1px solid ${footerMutedText}` }}>
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div
                className="text-sm mb-4 md:mb-0"
                style={{ color: footerMutedText, ...bodyFontStyle }}
              >
                {data.copyright ||
                  `© ${new Date().getFullYear()} ${data.siteName || 'Website'}. All rights reserved.`}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};
